import React, { useEffect, useState } from 'react';
import './Pagination.css';

// How many number/ellipsis slots the control is allowed, before the two arrows
// are added. Seven is comfortable on a desktop; a phone gets five, because the
// arrows and the numbers have to stay on one line together and a 375px screen
// does not have room for nine tap targets.
//
// Five is also the floor: first page, an ellipsis, the current page, another
// ellipsis and the last page is the smallest window that can still show where
// you are and both ends of the range.
const SLOTS_WIDE = 7;
const SLOTS_NARROW = 5;
const NARROW = '(max-width: 480px)';

// Window of numbered buttons around the current page. Keeps the control to a
// fixed width no matter how many pages there are, while always offering the
// first and last page directly. Never returns more than `maxSlots` entries,
// which is what keeps the row on one line.
export function pageWindow(page, pages, maxSlots) {
  if (pages <= maxSlots) return Array.from({ length: pages }, (_, i) => i + 1);

  // First and last are always shown, and in the worst case both ellipses are
  // too — whatever is left over is the run of pages around the current one.
  const run = Math.max(1, maxSlots - 4);
  let start = Math.max(2, page - Math.floor((run - 1) / 2));
  let end = start + run - 1;
  if (end > pages - 1) {
    end = pages - 1;
    start = Math.max(2, end - run + 1);
  }

  const out = [1];
  if (start > 2) out.push('gap-start');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < pages - 1) out.push('gap-end');
  out.push(pages);
  return out;
}

export default function Pagination({ page, pages, onChange }) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    // Re-read before subscribing: a width change between the first render and
    // this effect fires no event we are listening for yet.
    setNarrow(mq.matches);
    const handler = (e) => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (pages <= 1) return null;

  return (
    <nav className="pagination" aria-label="Business listing pages">
      <button
        type="button"
        className="pagination__btn pagination__btn--step"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label="Previous page"
      >‹</button>

      {pageWindow(page, pages, narrow ? SLOTS_NARROW : SLOTS_WIDE).map(item =>
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            className="pagination__btn"
            onClick={() => onChange(item)}
            aria-label={`Page ${item}`}
            aria-current={item === page ? 'page' : undefined}
          >{item}</button>
        ) : (
          <span key={item} className="pagination__gap" aria-hidden="true">…</span>
        )
      )}

      <button
        type="button"
        className="pagination__btn pagination__btn--step"
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
        aria-label="Next page"
      >›</button>
    </nav>
  );
}
